import React, { useId, useState } from 'react'
import styled from 'styled-components'
import invariant from 'tiny-invariant'
import { mergeProps } from 'react-aria'
import { IconMore } from '@chilipiper/icons/figma-generated/src/icon'
import { typeValidator } from '@chilipiper/utils'
import { Icon } from '../../../../core-components/icon/Icon'
import { Popover } from '../../../../core-components/popover/Popover'
import { UnstyledButton } from '../../../../core-components/unstyled-button/UnstyledButton'
import { ScreenReaderOnly } from '../../../../core-components'
import { Action } from '../../action-list'

export type ActionBarMoreButtonPublicProps = {
  children: React.ReactNode
  'data-id'?: string
  'data-test-id'?: string
}

type InternalProps = {
  INTERNAL_isOpen?: boolean
}

type Props = InternalProps & ActionBarMoreButtonPublicProps

export const ActionBarMoreButton = ({
  children,
  'data-id': dataId,
  INTERNAL_isOpen,
  'data-test-id': dataTestId,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const triggerId = useId()
  // check children before opening popover
  invariant(
    React.Children.toArray(children).every(
      child => typeValidator(Action.Item)(child) || typeValidator(Action.CascadeItem)(child)
    ),
    'All ActionBar.MoreButton children must be of type Action.Item or Action.CascadeItem'
  )

  const handleAction = () => setIsOpen(false)

  return (
    <Popover
      ariaProps={{ type: 'menu' }}
      placement='bottom start'
      offset={8}
      isOpen={INTERNAL_isOpen || isOpen}
      onOpenChange={setIsOpen}
      trigger={ariaProps => {
        return (
          <MoreButton
            data-id={dataId}
            data-test-id={dataTestId}
            display='flex'
            px={5}
            py={4}
            ariaProps={mergeProps(ariaProps, { id: triggerId })}
          >
            <ScreenReaderOnly>More actions</ScreenReaderOnly>
            <Icon icon={IconMore} color='fg/moderate' />
          </MoreButton>
        )
      }}
    >
      <Action.List ariaProps={{ 'aria-labelledby': triggerId }} onAction={handleAction}>
        {children}
      </Action.List>
    </Popover>
  )
}
ActionBarMoreButton.dsId = 'action-bar-more-button'

const MoreButton = styled(UnstyledButton)`
  position: relative;

  :hover,
  :focus-visible {
    background: ${({ theme }) => theme.colors['bg/action-bar-button-minor-hovered']};
  }

  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors['bg/action-bar-button-minor-active']};
    box-shadow: ${({ theme }) => theme.shadows['border/action-focused-minor']};
  }
`
